import Layout from '../../components/Layout';
import ProductCard from '../../components/ProductCard';
import DisclaimerNotice from '../../components/DisclaimerNotice';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

const products = [
  {
    name: "Sleep Mask",
    description: "Block out light for deeper, more restful sleep.",
    bestFor: "Better Sleep",
    benefits: ["Blocks light completely", "Comfortable fit", "Travel-friendly"]
  },
  {
    name: "Essential Oil Diffuser",
    description: "Create a calming atmosphere with your favorite essential oils.",
    bestFor: "Relaxation",
    benefits: ["Aromatherapy benefits", "Adjustable mist settings", "Quiet operation"]
  },
  {
    name: "Lavender Essential Oil",
    description: "A calming scent known for promoting relaxation and sleep.",
    bestFor: "Stress Relief",
    benefits: ["Calming properties", "Versatile use", "Natural formula"]
  },
  {
    name: "Dry Brush",
    description: "For dry brushing—a practice that may support circulation and exfoliation.",
    bestFor: "Body Care",
    benefits: ["Exfoliates skin", "May improve circulation", "Invigorating ritual"]
  },
  {
    name: "Weighted Blanket",
    description: "Deep pressure stimulation that may help with anxiety and sleep.",
    bestFor: "Anxiety & Sleep",
    benefits: ["Calming sensation", "May improve sleep", "Various weights available"]
  },
  {
    name: "Journal",
    description: "For gratitude journaling, reflection, or morning pages practice.",
    bestFor: "Mental Wellness",
    benefits: ["Promotes reflection", "Reduces stress", "Clarifies thoughts"]
  }
];

const SelfCareEssentials = () => {
  return (
    <Layout>
      {/* Hero */}
      <section data-testid="selfcare-hero" className="py-20 md:py-32 bg-[#F2EBE3]">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <Link 
            to="/wellness" 
            className="inline-flex items-center gap-2 text-[#3A4D39] text-sm font-medium mb-6 hover:underline"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Wellness
          </Link>
          <p className="text-xs tracking-widest uppercase font-medium text-[#C88D74] mb-4">
            Wellness
          </p>
          <h1 className="font-heading text-4xl sm:text-5xl font-semibold text-[#1C1C1C] leading-tight mb-6">
            Self-Care Essentials
          </h1>
          <p className="text-base md:text-lg text-[#374151] leading-relaxed">
            Products and tools for relaxation, stress relief, and daily self-care rituals. Small investments that can make a big difference in how you feel.
          </p>
        </div>
      </section>

      {/* Intro */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <h2 className="font-heading text-2xl md:text-3xl font-semibold text-[#1C1C1C] mb-4">
            The Power of Self-Care
          </h2>
          <p className="text-[#374151] leading-relaxed mb-4">
            Self-care isn't selfish—it's essential. Taking time for rest, relaxation, and activities that recharge you helps you show up better in all areas of life.
          </p>
          <p className="text-[#374151] leading-relaxed">
            These tools and products support common self-care practices, from better sleep to stress management to daily rituals that ground you.
          </p>
        </div>
      </section>

      {/* Products */}
      <section data-testid="selfcare-products" className="py-16 md:py-24 bg-[#F5F5F0]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <h2 className="font-heading text-2xl md:text-3xl font-semibold text-[#1C1C1C] mb-8">
            Self-Care Picks
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {products.map((product) => (
              <ProductCard key={product.name} {...product} />
            ))}
          </div>
        </div>
      </section>

      {/* How to Choose */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <h2 className="font-heading text-2xl md:text-3xl font-semibold text-[#1C1C1C] mb-6">
            How to Choose
          </h2>
          <ul className="space-y-4 text-[#374151]">
            <li className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 bg-[#3A4D39] rounded-full mt-2.5 flex-shrink-0" />
              <span><strong>Start with your pain points:</strong> What area of your wellbeing needs the most support right now?</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 bg-[#3A4D39] rounded-full mt-2.5 flex-shrink-0" />
              <span><strong>Quality matters:</strong> For items you'll use daily, invest in quality that will last.</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 bg-[#3A4D39] rounded-full mt-2.5 flex-shrink-0" />
              <span><strong>Consistency over products:</strong> The best self-care tool is the one you'll actually use regularly.</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 bg-[#3A4D39] rounded-full mt-2.5 flex-shrink-0" />
              <span><strong>Create rituals:</strong> Pair products with consistent times or activities to build habits.</span>
            </li>
          </ul>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="py-12 bg-[#F5F5F0]">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <DisclaimerNotice variant="inline" />
        </div>
      </section>
    </Layout>
  );
};

export default SelfCareEssentials;
